import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MdVerified } from 'react-icons/md';
import { CATEGORIES, REACTIONS } from '../../utils/constants';
import { resolveMediaUrl } from '../../utils/media';
import { reactionsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import './PostGalleryView.css';

const PostGalleryView = ({ post }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const category = CATEGORIES.find(cat => cat.id === post.category);
  const [reactionCounts, setReactionCounts] = useState(post?.reactions || {});
  const [userReaction, setUserReaction] = useState(null);

  const isVideo = post?.type === 'video-horizontal' || post?.type === 'video-reel';
  const isMeme = post?.type === 'meme';

  useEffect(() => {
    setReactionCounts(post?.reactions || {});
  }, [post?.reactions]);

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getPostSlug = () => {
    if (post?.slug) return post.slug;
    const base = slugify(post?.title) || 'post';
    return post?.id ? `${base}-${post.id}` : base;
  };

  const getPostRoute = () => {
    const slug = getPostSlug();
    if (post?.type === 'meme') return `/meme/${slug}`;
    if (post?.type === 'video-horizontal' || post?.type === 'video-reel') return `/video/${slug}`;
    return `/article/${slug}`;
  };

  const author = post?.author || {};
  const authorIsVerified = Boolean(author?.verification_level && author.verification_level !== 'none');
  const commentsCount = post?.comments ?? post?.commentsCount ?? post?.comments_count ?? 0;
  const loveCount = post?.reactions?.love ?? 0;
  const imageUrl = resolveMediaUrl(post?.image || post?.cover_image);
  const videoUrl = resolveMediaUrl(post?.video_url || post?.videoUrl);
  const linkState = isMeme ? undefined : { backgroundLocation: location };

  const handleReaction = async (reactionId) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para reaccionar');
      return;
    }
    try {
      const res = await reactionsAPI.toggle(post.id, reactionId);
      setUserReaction(res.data?.userReaction || null);
      setReactionCounts(res.data?.reactions || {});
    } catch (error) {
      console.error('Error toggling reaction:', error);
      toast.error('No se pudo reaccionar');
    }
  };

  return (
    <article className="post-gallery-view">
      <Link to={getPostRoute()} state={linkState} className="gallery-image-link">
        <div className="gallery-image">
          {isVideo && !imageUrl && videoUrl ? (
            <video
              src={videoUrl}
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={(e) => {
                try {
                  e.currentTarget.currentTime = 0.1;
                } catch (_) {
                  // ignore
                }
              }}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <img src={imageUrl} alt={post.title} loading="lazy" />
          )}
          <div className="gallery-overlay">
            {category && (
              <span className="gallery-category" style={{ backgroundColor: category.color }}>
                {category.icon} {category.name}
              </span>
            )}
          </div>
        </div>
      </Link>
      <div className="gallery-info">
        <Link to={getPostRoute()} state={linkState}>
          <h4 className="gallery-title">{post.title}</h4>
        </Link>

        <div className="gallery-meta">
          <div className="gallery-author">
            <span>
              {author?.name || 'Usuario'}
              {authorIsVerified && (
                <MdVerified
                  style={{
                    marginLeft: 6,
                    verticalAlign: 'middle',
                    color: author.badge_color || 'currentColor'
                  }}
                  title={author.badge_label || 'Cuenta verificada'}
                />
              )}
            </span>
          </div>
          <div className="gallery-stats">
            <span title="Corazones">❤️ {loveCount}</span>
            <span title="Comentarios">💬 {commentsCount}</span>
          </div>
        </div>

        <div className="gallery-reactions" aria-label="Reacciones">
          {REACTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`gallery-reaction-btn ${userReaction === r.id ? 'active' : ''}`}
              onClick={() => handleReaction(r.id)}
              title={r.label}
            >
              <span className="reaction-emoji">{r.emoji}</span>
              <span className="reaction-count">{reactionCounts?.[r.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>
    </article>
  );
};

export default PostGalleryView;
